import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  AlertTriangle, Plus, Edit, Trash2, Wrench, Clock, User, Play, 
  CheckCircle, Calendar, RefreshCw, XCircle, Search, Package, ChevronRight
} from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { Combobox } from '../../components/ui/combobox';
import { useIsMobile } from '../../hooks/useIsMobile';

const CorrectiveMaintenancePage = () => {
  const navigate = useNavigate();
  const { plants, selectedPlant, locations, selectedLocation } = useData();
  const isMobile = useIsMobile(1024);
  const [revisions, setRevisions] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [causes, setCauses] = useState([]);
  const [interventionTypes, setInterventionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  const emptyForm = {
    title: '',
    equipment_id: '',
    plant_id: '',
    location_id: '',
    scheduled_date: new Date().toISOString().slice(0, 16),
    description: '',
    assigned_to_ids: [],
    estimated_hours: '',
    symptom_id: '',
    cause_id: '',
    intervention_type_id: ''
  };
  
  const [formData, setFormData] = useState(emptyForm);

  const fetchDataQuiet = useCallback(async () => {
    try {
      const params = { revision_type: 'corrective' };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      const res = await api.get('/revisions', { params });
      setRevisions(res.data);
    } catch {}
  }, [selectedPlant, selectedLocation]);

  // Real-time: refresh on ticket updates (tickets can be transformed to revisions)
  useRealtimeEvent('ticket_update', fetchDataQuiet);
  useRealtimeEvent('new_ticket', fetchDataQuiet);

  useEffect(() => {
    fetchData();
  }, [selectedPlant, selectedLocation]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { revision_type: 'corrective' };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      
      const [revisionsRes, equipmentRes, usersRes, symptomsRes, causesRes, typesRes] = await Promise.all([
        api.get('/revisions', { params }),
        api.get('/equipment', { params: selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {} }),
        api.get('/users'),
        api.get('/symptoms').catch(() => ({ data: [] })),
        api.get('/causes').catch(() => ({ data: [] })),
        api.get('/intervention-types').catch(() => ({ data: [] }))
      ]);
      
      setRevisions(revisionsRes.data);
      setEquipment(equipmentRes.data);
      setUsers(usersRes.data.filter(u => ['technician', 'manager'].includes(u.role)));
      setSymptoms(symptomsRes.data);
      setCauses(causesRes.data);
      setInterventionTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedEquip = equipment.find(eq => eq.id === formData.equipment_id);
      
      const payload = {
        ...formData,
        revision_type: 'corrective',
        equipment_name: selectedEquip?.name || '',
        plant_id: selectedEquip?.plant_id || formData.plant_id,
        location_id: selectedEquip?.location_id || formData.location_id,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        assigned_to: formData.assigned_to_ids[0] || null,
        diagnostic: {
          symptom_id: formData.symptom_id,
          cause_id: formData.cause_id,
          intervention_type_id: formData.intervention_type_id
        },
        tasks: [],
        consumables: []
      };
      
      if (editItem) {
        await api.put(`/revisions/${editItem.id}`, payload);
        toast.success('Interventie actualizata!');
      } else {
        await api.post('/revisions', payload);
        toast.success('Interventie corectiva creata!');
      }
      
      setShowAddDialog(false);
      setEditItem(null);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title || '',
      equipment_id: item.equipment_id || '',
      plant_id: item.plant_id || '',
      location_id: item.location_id || '',
      scheduled_date: item.scheduled_date ? item.scheduled_date.split('T')[0] + 'T' + (item.scheduled_date.split('T')[1]?.slice(0,5) || '08:00') : '',
      description: item.description || '',
      assigned_to_ids: item.assigned_to_ids || (item.assigned_to ? [item.assigned_to] : []),
      estimated_hours: item.estimated_hours || '',
      symptom_id: item.diagnostic?.symptom_id || '',
      cause_id: item.diagnostic?.cause_id || '',
      intervention_type_id: item.diagnostic?.intervention_type_id || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/revisions/${id}`);
      toast.success('Interventie stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleStatusChange = async (revisionId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'in_progress') {
        updateData.started_at = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }
      await api.put(`/revisions/${revisionId}`, updateData);
      toast.success('Status actualizat!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const toggleTechnician = (userId) => {
    if (formData.assigned_to_ids.includes(userId)) {
      setFormData({ ...formData, assigned_to_ids: formData.assigned_to_ids.filter(id => id !== userId) });
    } else {
      setFormData({ ...formData, assigned_to_ids: [...formData.assigned_to_ids, userId] });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-slate-100 text-slate-700',
      waiting_parts: 'bg-orange-100 text-orange-700',
      rejected_by_technician: 'bg-red-100 text-red-700'
    };
    const labels = {
      planned: 'Planificat',
      in_progress: 'In lucru',
      completed: 'Finalizat',
      cancelled: 'Anulat',
      waiting_parts: 'Asteapta piese',
      rejected_by_technician: 'Refuzat'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const tableColumns = [
    {
      key: 'title',
      label: 'Denumire Interventie',
      sortable: true,
      filterable: true,
      render: (value, row) => row ? (
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          <span className="font-medium">{value}</span>
        </div>
      ) : '-'
    },
    {
      key: 'equipment_name',
      label: 'Echipament',
      sortable: true,
      filterable: true
    },
    {
      key: 'scheduled_date',
      label: 'Data Planificata',
      sortable: true,
      render: (value, row) => row ? formatDate(value) : '-'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterOptions: [
        { value: 'planned', label: 'Planificat' },
        { value: 'in_progress', label: 'In lucru' },
        { value: 'completed', label: 'Finalizat' },
        { value: 'waiting_parts', label: 'Asteapta piese' }
      ],
      render: (value, row) => row ? getStatusBadge(value) : '-'
    },
    {
      key: 'assigned_to_name',
      label: 'Responsabil',
      sortable: true,
      filterable: true,
      render: (value, row) => {
        if (!row) return '-';
        if (row.assigned_to_ids?.length > 1) {
          return <Badge variant="outline">{row.assigned_to_ids.length} tehnicieni</Badge>;
        }
        return value || '-';
      }
    },
    {
      key: 'estimated_hours',
      label: 'Ore Est.',
      sortable: true,
      render: (value, row) => row?.estimated_hours ? `${row.estimated_hours}h` : '-'
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      render: (_, row) => row ? (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {row.status === 'planned' && (
            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(row.id, 'in_progress')} className="text-purple-600" title="Incepe">
              <Play className="w-4 h-4" />
            </Button>
          )}
          {row.status === 'in_progress' && (
            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(row.id, 'completed')} className="text-green-600" title="Finalizeaza">
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} title="Editeaza">
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600" title="Sterge">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : null
    }
  ];

  // Statistics
  const stats = {
    total: revisions.length,
    planned: revisions.filter(r => r.status === 'planned').length,
    inProgress: revisions.filter(r => r.status === 'in_progress').length,
    completed: revisions.filter(r => r.status === 'completed').length
  };

  return (
    <div className="space-y-6" data-testid="corrective-maintenance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Mentenanta Corectiva</h1>
          <p className="text-slate-600 mt-1">Interventii pentru repararea defectiunilor si avariilor</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="h-11">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizeaza
          </Button>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditItem(null); setFormData(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-11">
                <Plus className="w-5 h-5 mr-2" />
                INTERVENTIE NOUA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editItem ? 'Editeaza Interventie' : 'Adauga Interventie Corectiva'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Denumire interventie *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="h-11"
                    placeholder="Ex: Defect motor pompa"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Echipament *</Label>
                    <Combobox
                      items={equipment}
                      value={formData.equipment_id}
                      onSelect={(id) => setFormData({ ...formData, equipment_id: id })}
                      placeholder="Selecteaza echipamentul..."
                      searchPlaceholder="Cauta echipament..."
                      emptyText="Niciun echipament gasit."
                      codeKey="code"
                      secondaryKey="location_name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tip interventie</Label>
                    <Combobox
                      items={interventionTypes}
                      value={formData.intervention_type_id}
                      onSelect={(id) => setFormData({ ...formData, intervention_type_id: id })}
                      placeholder="Selecteaza tipul..."
                      searchPlaceholder="Cauta tip interventie..."
                      emptyText="Niciun tip gasit."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Simptom</Label>
                    <Combobox
                      items={symptoms}
                      value={formData.symptom_id}
                      onSelect={(id) => setFormData({ ...formData, symptom_id: id })}
                      placeholder="Selecteaza simptomul..."
                      searchPlaceholder="Cauta simptom..."
                      emptyText="Niciun simptom gasit."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cauza</Label>
                    <Combobox
                      items={causes}
                      value={formData.cause_id}
                      onSelect={(id) => setFormData({ ...formData, cause_id: id })}
                      placeholder="Selecteaza cauza..."
                      searchPlaceholder="Cauta cauza..."
                      emptyText="Niciun cauza gasita."
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Tehnicieni responsabili</Label>
                  <div className="flex flex-wrap gap-2">
                    {users.map(user => (
                      <Badge 
                        key={user.id}
                        variant={formData.assigned_to_ids.includes(user.id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleTechnician(user.id)}
                      >
                        <User className="w-3 h-3 mr-1" />
                        {user.full_name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data si ora planificata *</Label>
                    <Input 
                      type="datetime-local"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ore estimate</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                      className="h-11"
                      placeholder="2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descriere defect</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Descrieti defectul constatat..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Inapoi</Button>
                  <Button type="submit" className="btn-primary">Salveaza</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.planned}</p>
                <p className="text-sm text-slate-600">Planificate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
                <p className="text-sm text-slate-600">In lucru</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-sm text-slate-600">Finalizate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : isMobile ? (
        <div className="space-y-2" data-testid="corrective-mobile-list">
          {revisions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Nu exista interventii corective</div>
          ) : revisions.map((rev) => (
            <div
              key={rev.id}
              className="bg-white rounded-xl border border-slate-200 p-3.5 active:bg-slate-50 transition-colors"
              onClick={() => navigate(`/revisions/${rev.id}`)}
              data-testid={`mobile-corrective-${rev.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <h3 className="text-sm font-semibold text-slate-900 line-clamp-1 flex-1">{rev.title}</h3>
                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
              </div>
              <p className="text-xs text-slate-500 line-clamp-1 mb-2">{rev.equipment_name || '-'}</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {getStatusBadge(rev.status)}
                {rev.scheduled_date && (
                  <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                    <Calendar className="w-3 h-3" />{new Date(rev.scheduled_date).toLocaleDateString('ro-RO')}
                  </span>
                )}
              </div>
              {rev.assigned_to_name && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1">
                  <User className="w-3 h-3 shrink-0" />{rev.assigned_to_name}
                  {rev.estimated_hours && <span className="ml-auto">{rev.estimated_hours}h est.</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <DataTable 
          data={revisions} 
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          onRowClick={(row) => navigate(`/revisions/${row.id}`)}
          emptyMessage="Nu exista interventii corective. Creati prima interventie!"
        />
      )}
    </div>
  );
};

export default CorrectiveMaintenancePage;
